/**
 * CarrierCounterModelContent — self-contained wrapper around the existing
 * CarrierCounterModelSection so it can mount by reportId alone inside the
 * accountant Case Workspace Defense stage.
 *
 * CarrierCounterModelSection is prop-driven for its grid seed (financial_data +
 * coverage_year) — in the standalone AccountantReportView those come from the
 * already-fetched report. Here we fetch the same accountant report endpoint
 * (GET /api/v1/collaboration/reports/:id/view) to supply them, matching that
 * consumer's contract exactly (getAuthHeader + formatCurrency + financialData +
 * coverageYear). Zero net-new dashboards — the section itself is unchanged.
 *
 * Auth is role-agnostic (no AccountantAuthProvider required): getAuthHeader is a
 * local sync getter preferring the accountant localStorage token, while the real
 * fetches (here and in the child) use the role-agnostic async getAuthHeaders().
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import axios from '@/lib/http';
import { getAuthHeaders } from '../../lib/authHeaders';
import { formatCurrency } from '@/utils/financialFormat';
import { getCaseProfile } from '../../services/caseProfileService';
import type { WaterfallGrossBasis } from '../../services/adversarialReclassificationApi';
import CarrierCounterModelSection from './CarrierCounterModelSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export interface CarrierCounterModelContentProps {
  reportId?: string;
  showHeader?: boolean;
}

export function CarrierCounterModelContent({
  reportId: reportIdProp,
  showHeader = true,
}: CarrierCounterModelContentProps) {
  const { reportId: reportIdParam } = useParams<{ reportId: string }>();
  const reportId = reportIdProp ?? reportIdParam;

  // Role-agnostic sync header getter — no AccountantAuthProvider dependency, so
  // this panel mounts in ANY workspace (accountant / attorney / owner) without
  // useAccountantAuth() throwing. Mirrors lib/authHeaders' localStorage-token
  // preference. The child's REAL fetches use the async getAuthHeaders() (which
  // adds the Clerk fallback); this prop is only a best-effort sync header.
  const getAuthHeader = React.useCallback((): { Authorization: string } | {} => {
    try {
      const token = localStorage.getItem('accountant_token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
      return {};
    }
  }, []);

  const [financialData, setFinancialData] = useState<any>(undefined);
  const [coverageYear, setCoverageYear] = useState<number | undefined>(undefined);
  const [initialBasis, setInitialBasis] = useState<WaterfallGrossBasis>('bi');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!reportId) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await axios.get(
          `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/view`,
          { headers: await getAuthHeaders() }
        );
        if (cancelled) return;
        setFinancialData(resp.data?.financial_data);
        setCoverageYear(resp.data?.coverage_year);

        // Ground the policy-waterfall gross-basis DEFAULT in the resolver
        // (GET /case-profile), not a hard-coded 'bi'. Only damages_model can
        // drive it: 'bi' is always a pillar, so property is never the default —
        // it stays a user toggle. future_closure → 'fc'; else 'bi'.
        const profile = await getCaseProfile(reportId, { headers: await getAuthHeaders() });
        if (!cancelled) {
          setInitialBasis(profile?.damages_model === 'future_closure' ? 'fc' : 'bi');
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err?.message || 'Failed to load case financials');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // getAuthHeader is stable from context; reportId is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  return (
    <div className={showHeader ? 'min-h-screen' : ''} style={{ background: 'var(--color-bg-base)' }} data-testid="ccm-content">
      {showHeader && (
        <div className="px-6 py-4" style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
          <div className="mx-auto max-w-5xl">
            <h1 className="cs-h2">Carrier Counter-Model</h1>
            <p className="cs-meta mt-1">
              Model the carrier's most likely reductions against our position — the rebuttal workbench.
            </p>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-5xl px-6 py-6">
        {loading && (
          <div className="cs-meta flex items-center gap-2" style={{ color: 'var(--color-text-muted)' }} data-testid="ccm-content-loading">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading case financials…
          </div>
        )}

        {!loading && error && (
          <div
            className="cs-body flex items-start gap-2 rounded-lg px-4 py-3"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
            data-testid="ccm-content-error"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: 'var(--color-blocker)' }} />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && reportId && (
          <CarrierCounterModelSection
            reportId={reportId}
            getAuthHeader={getAuthHeader}
            formatCurrency={formatCurrency}
            financialData={financialData}
            coverageYear={coverageYear}
            initialBasis={initialBasis}
          />
        )}
      </div>
    </div>
  );
}

export default CarrierCounterModelContent;
